'use client'
import { SbBlokData, storyblokEditable, StoryblokServerComponent } from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'

import { PhnxAuthorcard } from '@/components/02-molecules/Authorcard/Authorcard'

import { PhnxService } from './Service'
import { ServiceProps } from './Service'

interface ServiceBlokProps {
  blok: {
    title: ServiceProps['title']
    subtitle: ServiceProps['subtitle']
    description: ServiceProps['description']
    heroAsset: any
    washOpacity?: number
    frameSrc: ServiceProps['frameSrc']
    heroForm: ServiceProps['heroForm'] | any
    hero_button: any
    reviews: any
    featureContent: any
    steps: any
    stepsTitle: string
    services: any
    servicesTitle: string
    servicesSubtitle: string
    cta: any
    relatedArticles: any
    relatedArticlesTitle: string
    relatedArticlesSubtitle: string
    belowContent: any
    serviceItemsPerRow: string
  } & SbBlokData
  rels: any
  name: string
}

const Service: FC<ServiceBlokProps> = ({ blok, rels, name }) => {
  const [reviews, setReviews] = useState()
  const [steps, setSteps] = useState()
  const [services, setServices] = useState()
  const [relatedArticles, setRelatedArticles] = useState()

  useEffect(() => {
    if (blok.reviews) {
      const reviewsArray = blok.reviews.map((review: any) => {
        let rating = review?.rating ? Number(review.rating) : 0
        return {
          rating: rating,
          content: review.content
            ? review.content.map((content: any) => (
                <StoryblokServerComponent key={content._uid} blok={content} />
              ))
            : null,
          author: review.author,
        }
      })
      setReviews(reviewsArray)
    }
  }, [blok.reviews])

  useEffect(() => {
    if (blok.steps) {
      const stepsArray = blok.steps.map((step: any) => {
        return {
          title: step.title,
          description: step.description,
          asset: { src: step?.asset?.filename },
        }
      })
      setSteps(stepsArray)
    }
  }, [blok.steps])

  useEffect(() => {
    if (!blok.relatedArticles || !rels) return

    const getAuthor = (authorUID: string) => {
      const author = rels.find((rel: any) => rel.uuid === authorUID)
      if (!author) return null

      return (
        <PhnxAuthorcard
          name={author.name}
          layout="compact"
          asset={{
            src: author.content.image?.filename,
            alt: author.content.image?.alt,
          }}
        />
      )
    }

    const relatedArticlesArray = blok.relatedArticles
      .map((article: any) => {
        const content = article.content
        if (content && content.title !== undefined && content.title !== '') {
          return {
            asset: {
              src: content.card_asset?.filename || content.featured_image?.filename,
              alt: content.card_asset?.alt || content.featured_image?.alt,
            },
            author: content.author && getAuthor(content.author.uuid),
            title: content.title,
            tags: content.tags,
            link: `/${article.full_slug}`,
          }
        }
        return null
      })
      .filter(Boolean)

    setRelatedArticles(relatedArticlesArray)
  }, [blok.relatedArticles, rels])

  useEffect(() => {
    if (blok.services) {
      const servicesArray = blok.services.map((service: any) => {
        return {
          title: service.title,
          description: service.description,
          body:
            service.body &&
            service.body.map((nestedBlok: SbBlokData) => (
              <StoryblokServerComponent blok={nestedBlok} key={nestedBlok._uid} />
            )),
          link: { url: service.link.cached_url, label: service.linkLabel },
          asset: {
            src: service.asset.filename,
            alt: service.asset.alt,
            focus: service.asset.focus,
          },
        }
      })
      setServices(servicesArray)
    }
  }, [blok.services])

  return (
    <div {...storyblokEditable(blok)}>
      {!blok.heroAsset && <h1 className={'sr-only'}>{name}</h1>}
      <PhnxService
        title={blok.title}
        subtitle={blok.subtitle}
        description={blok.description}
        heroAsset={
          blok.heroAsset
            ? {
                src: blok.heroAsset.filename,
                alt: blok.heroAsset.alt,
                focus: blok.heroAsset.focus,
                washOpacity: blok.washOpacity ? blok.washOpacity : 0.5,
              }
            : undefined
        }
        heroButton={
          blok.hero_button
            ? blok.hero_button.map((content: any) => (
                <StoryblokServerComponent key={content._uid} blok={content} />
              ))
            : null
        }
        frameSrc={blok.frameSrc}
        heroForm={
          blok.heroForm &&
          blok.heroForm.map((content: any) => (
            <StoryblokServerComponent key={content._uid} blok={content} />
          ))
        }
        reviews={reviews}
        featureContent={
          blok.featureContent
            ? blok.featureContent.map((content: any) => (
                <StoryblokServerComponent key={content._uid} blok={content} />
              ))
            : null
        }
        steps={steps}
        stepsTitle={blok.stepsTitle && blok.stepsTitle}
        services={services}
        serviceItemsPerRow={blok.serviceItemsPerRow as 'one' | 'two' | 'three' | 'four'}
        servicesTitle={blok.servicesTitle && blok.servicesTitle}
        servicesSubtitle={blok.servicesSubtitle && blok.servicesSubtitle}
        cta={
          blok.cta
            ? blok.cta.map((content: any) => (
                <StoryblokServerComponent key={content._uid} blok={content} />
              ))
            : null
        }
        relatedArticles={relatedArticles}
        relatedArticlesTitle={blok.relatedArticlesTitle && blok.relatedArticlesTitle}
        relatedArticlesSubtitle={blok.relatedArticlesSubtitle && blok.relatedArticlesSubtitle}
        belowContent={
          blok.belowContent
            ? blok.belowContent.map((content: any) => (
                <StoryblokServerComponent key={content._uid} blok={content} />
              ))
            : null
        }
      />
    </div>
  )
}

export default Service
